import type {
  RangedFieldTypes,
  ResultRecord,
} from '@dynatrace-sdk/client-query';

import type { TimeseriesWithDimensions } from './convert-to-timeseries.js';
import { createTimeseriesFromRecord } from './timeseries/create-timeseries-from-record.js';
import type { Dimension } from './timeseries/get-dimensions.js';
import type { Timeseries } from '../charts/core/types/timeseries.js';
import type {
  TimeseriesBand,
  TimeseriesBandDataPoint,
} from '../charts/timeseries/types/timeseries-band.js';

const upperSuffix = ['max', 'upper'];
const lowerSuffix = ['min', 'lower'];

/**
 * @internal
 */
export interface TimeseriesBandWithDimensions extends TimeseriesBand {
  userData: {
    dimensions: Dimension[];
    valueDimension: string;
    lowerValueDimension: string;
    upperValueDimension: string;
  };
}

function valueDimensionEndsWith(
  timeseries: TimeseriesWithDimensions,
  suffixList: string[],
) {
  return suffixList.some((suffix) =>
    timeseries.userData.valueDimension.endsWith(suffix),
  );
}

function getDimensionsWithoutLast(name: string | string[]): string[] | string {
  if (typeof name === 'string') {
    return 'band';
  }
  const timeseriesName = name.slice(0, -1);
  return timeseriesName.length > 0 ? timeseriesName : 'band';
}

/**
 * Converts DUES query result to a timeseries band.
 * @param record - Record from a query
 * @param types - Field types
 * @public
 */
export function convertToTimeseriesBand(
  record: ResultRecord,
  types: RangedFieldTypes,
):
  | {
      timeseriesBand: TimeseriesBand;
      timeseries: Timeseries[];
      timeseriesOfBand: [Timeseries, Timeseries];
    }
  | undefined {
  const timeseries = createTimeseriesFromRecord(record, types);

  const upper = timeseries.find((timeseries) =>
    valueDimensionEndsWith(timeseries, upperSuffix),
  );

  const lower = timeseries.find((timeseries) =>
    valueDimensionEndsWith(timeseries, lowerSuffix),
  );

  const rest = timeseries.filter(
    (timeseries) => timeseries !== lower && timeseries !== upper,
  );

  if (upper && lower && upper.datapoints.length === lower.datapoints.length) {
    const timeseriesBand: TimeseriesBandWithDimensions = {
      name: getDimensionsWithoutLast(upper.name),
      datapoints: upper.datapoints.map(
        ({ value, ...datapoint }, index): TimeseriesBandDataPoint => ({
          ...datapoint,
          y0: lower?.datapoints[index].value,
          y1: value,
        }),
      ),
      userData: {
        ...upper.userData,
        lowerValueDimension: lower.userData.valueDimension,
        upperValueDimension: upper.userData.valueDimension,
      },
    };
    return {
      timeseriesBand,
      timeseries: rest,
      timeseriesOfBand: [upper, lower],
    };
  }
}
